// External Libraries
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CSVLink } from "react-csv";

// Material-UI Components
import { Container, Card, CardContent, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';

// Internal Components
import MainTable from "../table/MainTable";

/************* Tebra Patients Table *************/

const TebraPatients = ({
  enableEditing,
  renderRowActions,
  editDisplayMode,
  columnPinning,
  positionActionsColumn,
  enableRowActions,
  displayColumnDefOptions,
  onEditingRowSave,
  notFoundPatientData,
  renderEditRowDialogContent,
  isLoadingMapPatient
}) => {
  const [tebraPatientData, setTebraPatientData] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 1, pageSize: 10, totalPages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const initialLoadComplete = useRef(false);

  const fetchPatients = async (pageIndex, query = '') => {
    try {
      setIsLoading(true);
      const url = `${process.env.REACT_APP_APIS_BASE_URL}/api/tebra-patients?page=${pageIndex}`;
      const response = await axios.get(
        query ? `${process.env.REACT_APP_APIS_BASE_URL}/api/tebra-patients/${query}?page=${pageIndex}` : url,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      const patientStatus = response.data.data.data;

      setTebraPatientData(patientStatus);
      setPagination((prev) => ({
        ...prev,
        pageIndex: response.data.data.current_page,
        totalPages: response.data.data.last_page,
      }));
    } catch (err) {
      console.error("Error fetching patients:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialLoadComplete.current || !isLoading) {
      initialLoadComplete.current = true;
      fetchPatients(pagination.pageIndex, query);
    }
  }, [pagination.pageIndex, query]);

  /* const mappingPatient = async (table, row) => {
    try {
      const tebraTableID = row.original.TebraID;
      const acPatientID = notFoundPatientData.acDemographicPatient.PatientID;

      if (!acPatientID) {
        console.error('Missing required PatientID for mapping');
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_APIS_BASE_URL}/api/update-patient`,
        {
          TebraID: tebraTableID,
          MedicalRecordNumber: acPatientID
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        }
      );

      if (response.data.status) {
        console.log('Patient mapped successfully');
        fetchPatients(pagination.pageIndex);
        table.setEditingRow(null);
      } else {
        console.error(response.data.message || 'Failed to map patient');
      }
    } catch (error) {
      console.error('Error mapping patient:', error);
    }
  }; */

  const handlePageChange = (newPageIndex) => {
    if (typeof newPageIndex === 'object' && newPageIndex.hasOwnProperty('pageIndex')) {
      newPageIndex = newPageIndex.pageIndex;
    }

    if (typeof newPageIndex === 'number' && !isNaN(newPageIndex)) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: newPageIndex + 1,
      }));
    } else {
      console.error("Invalid page index passed:", newPageIndex);
    }
  };

  const handleQueryChange = (queryValue) => {
    setQuery(queryValue);
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
  };

  const columns = [
    {
      accessorKey: 'TebraID',
      header: 'TebraID',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'FirstName',
      header: 'FirstName',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'MiddleName',
      header: 'MiddleName',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'LastName',
      header: 'LastName',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'PatientFullName',
      header: 'PatientFullName',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'DOB',
      header: 'DOB',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'Gender',
      header: 'Gender',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'EmailAddress',
      header: 'EmailAddress',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'HomePhone',
      header: 'HomePhone',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'WorkPhone',
      header: 'WorkPhone',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'AddressLine1',
      header: 'AddressLine1',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'City',
      header: 'City',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'State',
      header: 'State',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'ZipCode',
      header: 'ZipCode',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'MedicalRecordNumber',
      header: 'MedicalRecordNumber',
      Edit: () => (
        <TextField
          label="Tebra Medical Record"
          value={notFoundPatientData.acDemographicPatient.PatientID}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'PrimaryInsurancePolicyCompanyName',
      header: 'PrimaryInsurancePolicyCompanyName',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'PrimaryInsurancePolicyNumber',
      header: 'PrimaryInsurancePolicyNumber',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'SecondaryInsurancePolicyCompanyName',
      header: 'SecondaryInsurancePolicyCompanyName',
      enableEditing: false,
      Edit: () => null,
    },
    {
      accessorKey: 'SecondaryInsurancePolicyNumber',
      header: 'SecondaryInsurancePolicyNumber',
      enableEditing: false,
      Edit: () => null,
    },
  ];

  const csvData = tebraPatientData.map(patient => {
    const row = {};
    columns.forEach(column => {
      row[column.header] = patient[column.accessorKey] || '';
    });
    return row;
  });

  const renderTopToolbarCustomActions = () => (
    <div className="d-flex gap-2">
      <CSVLink
        data={csvData}
        filename={"patients_data.csv"}
        className="btn btn-primary light btn-sm"
      >
        <i className="fa-solid fa-file-excel me-2" />
        Export Report
      </CSVLink>
    </div>
  );

  return (
    <>
      <Container maxWidth="xxl" sx={{ padding: '1.875rem 1.875rem 0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: '12px' }}>
              <CardContent>
                <MainTable
                  data={tebraPatientData}
                  columns={columns}
                  title="Tebra Patients"

                  enableRowSelection={false}
                  enableColumnFilters={true}
                  enableGlobalFilter={true}
                  enablePagination={true}
                  enableSorting={true}
                  enableGrouping={true}
                  manualPaginationMode={true}
                  paginationDisplayMode="pages"
                  currentPage={pagination.pageIndex - 1}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}

                  isLoading={isLoadingMapPatient}

                  manualFiltering={true}
                  onGlobalFilterChange={handleQueryChange}
                  globalFilterValue={query}

                  enableStickyHeader={true}
                  enableStickyFooter={true}
                  enableColumnResizing={false}
                  enableHiding={true}

                  rowsPerPageOptions={[10, 25, 50, 100]}
                  initialPageSize={10}

                  renderTopToolbarCustomActions={renderTopToolbarCustomActions}
                  enableEditing={enableEditing}
                  onEditingRowSave={onEditingRowSave}
                  renderRowActions={renderRowActions}
                  editDisplayMode={editDisplayMode}
                  columnPinning={columnPinning}
                  positionActionsColumn={positionActionsColumn}
                  enableRowActions={enableRowActions}
                  displayColumnDefOptions={displayColumnDefOptions}
                  renderEditRowDialogContent={renderEditRowDialogContent}

                  muiTablePaperProps={{
                    sx: {
                      boxShadow: 'none',
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default TebraPatients;
